import axios from 'axios';
import { coreApi } from '@shared/api/coreApi';
import { tenantApi } from '@shared/api/tenantApi';
import { useAuthStore } from '@shared/stores/authStore';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { useModuleStore } from '@shared/stores/moduleStore';
import { resolveDomain, loadDomainConfig, domainForResolution, effectiveDefaultDomain } from '@shared/api/domainApi';
import {
  applyLocalDevBootstrap,
  shouldUseDirectTenantLogin,
} from '@shared/lib/localDevBootstrap';
import { getEffectiveRootTenantId } from '@shared/lib/ssoRootScope';
import { env } from '@config/env';
import type { CorePortalAuthResponse, AuthResponse } from '@shared/types/auth';
import type { LoginValues, ForgotValues, ResetValues } from './schema';
import { queryClient } from '@app/providers';
import { hydrateMemberProfile } from '@features/profile/api';

/**
 * Auth API surface. Mirrors the Angular `AuthService` flows:
 *   - login           : Core gateway when authMode='core', else direct Tenant /auth/login
 *   - signup          : signup via Core /portal-auth/signup; tenant linking via SSO
 *   - forgot/reset    : email-based password reset via Core
 *   - switchTenant    : re-resolve regional API URL + branding for new tenant
 *   - registerDevice  : push notification token registration (Phase E)
 *
 * All write paths drive Zustand stores directly so callers don't have to
 * thread responses through. Branding overlays are computed centrally.
 */

interface LoginPayload {
  identifier: string;
  password: string;
  method?: 'email' | 'mobile';
}

/** Detect whether an identifier is an email address or a mobile number. */
export function detectMethod(identifier: string): 'email' | 'mobile' {
  return identifier.includes('@') ? 'email' : 'mobile';
}

function loadAuthedDomainConfig(domain?: string | null): void {
  const target =
    domain?.trim() ||
    domainForResolution(typeof window !== 'undefined' ? window.location.hostname : '');
  if (!target) return;
  void loadDomainConfig(target).catch((error) => {
    console.warn('[auth] domain-config load after auth failed:', error);
  });
}

async function ensureLocalDevAuthContext(): Promise<void> {
  applyLocalDevBootstrap();
  const auth = useAuthStore.getState();
  if (!auth.getRegionalApiUrl()) {
    auth.setRegionalApiUrl(env.apiBaseUrl);
  }
  if (!auth.getTenantId()) {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const domain = domainForResolution(hostname);
    if (domain) {
      try {
        await resolveDomain(domain, { persistSsoFlags: false });
      } catch (error) {
        console.warn('[auth] local dev domain resolve failed:', error);
      }
    }
  }
}

export async function login(values: LoginValues): Promise<AuthResponse> {
  applyLocalDevBootstrap();
  const remember = values.remember ?? true;

  const payload: LoginPayload = {
    identifier: values.identifier,
    password: values.password,
    method: detectMethod(values.identifier),
  };

  if (shouldUseDirectTenantLogin()) {
    await ensureLocalDevAuthContext();
    return loginViaTenant(payload, remember);
  }

  if (env.authMode === 'core') {
    return loginViaCore(payload, remember);
  }

  // Tenant mode: resolve domain first if we don't have one cached.
  const auth = useAuthStore.getState();
  if (!auth.regionalApiUrl) {
    await resolveDomain(window.location.hostname);
  }
  return loginViaTenant(payload, remember);
}

async function loginViaCore(payload: LoginPayload, remember: boolean): Promise<AuthResponse> {
  const body: Record<string, unknown> = {
    identifier: payload.identifier,
    method: payload.method ?? 'email',
    password: payload.password,
    owner_type: env.portalOwnerType ?? 'member',
  };
  if (payload.method === 'email' || !payload.method) {
    body.email = payload.identifier;
  }
  const rootScope = getEffectiveRootTenantId(env.rootTenantId);
  if (rootScope) {
    body.root_tenant_id = rootScope;
  }

  const { data } = await coreApi.post<CorePortalAuthResponse>('/portal-auth/login', body);
  if (!data.success) throw new Error('Login failed');

  await applyAuthSuccess(data, remember);
  return data;
}

async function loginViaTenant(payload: LoginPayload, remember: boolean): Promise<AuthResponse> {
  const { data } = await tenantApi.post<AuthResponse>('/auth/login', {
    email: payload.method === 'email' ? payload.identifier.trim().toLowerCase() : undefined,
    mobile: payload.method === 'mobile' ? payload.identifier : undefined,
    password: payload.password,
    owner_type: env.portalOwnerType ?? 'member',
  });
  if (!data.success) throw new Error('Login failed');

  await useAuthStore.getState().setAuth(data, { remember });
  void hydrateMemberProfile();
  loadAuthedDomainConfig();
  return data;
}

/**
 * Apply Core SSO login response to all relevant stores: auth, branding,
 * SSO links, regional API URL, and (asynchronously) domain config.
 */
async function applyAuthSuccess(response: CorePortalAuthResponse, remember: boolean): Promise<void> {
  const auth = useAuthStore.getState();
  const branding = useBrandingStore.getState();

  await auth.setAuth(response, { remember });
  await auth.setSso(response, { remember });

  if (response.branding && response.domain) {
    const hubDomain = effectiveDefaultDomain();
    const isHubLogin =
      (hubDomain && response.domain === hubDomain) ||
      (!hubDomain && response.is_root_tenant === true);
    if (isHubLogin) {
      branding.setBaseBrandingFromResolution(
        response.domain,
        response.branding.name,
        response.branding.primary_color,
        response.branding,
      );
    }
    branding.setBranding(
      response.domain,
      response.branding.name,
      response.branding.primary_color,
      response.branding,
    );
  }

  // Persist the active portal/community domain so a (native) app reopen restores
  // THIS tenant's branding + context via domainForResolution, instead of
  // re-resolving the configured hub domain and reverting to hub branding/tenant.
  // Covers both direct login and switchTenant (which routes through here).
  if (response.domain) {
    try {
      localStorage.setItem('paas_active_portal_domain', response.domain);
    } catch {
      /* storage unavailable */
    }
  }

  void hydrateMemberProfile();

  // Fire-and-forget: tenant domain config carries module flags + experience.
  loadAuthedDomainConfig(response.domain);
}

/**
 * Request a one-time login code for an identifier (email or mobile).
 * Mirrors `AuthService.requestLoginOtp` — Core gateway in `core` auth
 * mode, regional `/auth/otp/request` otherwise.
 */
export async function requestLoginOtp(identifier: string): Promise<void> {
  const method = detectMethod(identifier);
  const normalized = method === 'email' ? identifier.trim().toLowerCase() : identifier.trim();
  const ownerType = env.portalOwnerType ?? 'member';

  if (!shouldUseDirectTenantLogin() && env.authMode === 'core') {
    const body: Record<string, unknown> = { identifier: normalized, method, owner_type: ownerType };
    const rootScope = getEffectiveRootTenantId(env.rootTenantId);
    if (rootScope) body.root_tenant_id = rootScope;
    const domain = domainForResolution(window.location.hostname);
    if (domain) body.domain = domain;
    await coreApi.post('/portal-auth/otp/request', body);
    return;
  }
  await ensureLocalDevAuthContext();
  await tenantApi.post('/auth/otp/request', {
    identifier: normalized,
    method,
    owner_type: ownerType,
  });
}

/** Verify a one-time login code and complete sign-in. */
export async function verifyLoginOtp(identifier: string, otpCode: string): Promise<AuthResponse> {
  const method = detectMethod(identifier);
  const normalized = method === 'email' ? identifier.trim().toLowerCase() : identifier.trim();
  const ownerType = env.portalOwnerType ?? 'member';

  if (!shouldUseDirectTenantLogin() && env.authMode === 'core') {
    const body: Record<string, unknown> = {
      identifier: normalized,
      otp_code: otpCode.trim(),
      method,
      owner_type: ownerType,
    };
    const rootScope = getEffectiveRootTenantId(env.rootTenantId);
    if (rootScope) body.root_tenant_id = rootScope;
    const domain = domainForResolution(window.location.hostname);
    if (domain) body.domain = domain;
    const { data } = await coreApi.post<CorePortalAuthResponse>('/portal-auth/otp/verify-login', body);
    if (!data.success) throw new Error('Verification failed');
    await applyAuthSuccess(data, useAuthStore.getState().rememberSession);
    return data;
  }
  await ensureLocalDevAuthContext();

  const { data } = await tenantApi.post<AuthResponse>('/auth/otp/verify-login', {
    identifier: normalized,
    otp_code: otpCode.trim(),
    method,
    owner_type: ownerType,
  });
  if (!data.success) throw new Error('Verification failed');
  await useAuthStore.getState().setAuth(data, { remember: useAuthStore.getState().rememberSession });
  loadAuthedDomainConfig();
  return data;
}

/**
 * Switch to a different linked tenant. Re-resolves regional API URL,
 * applies new branding overlay, invalidates all cached queries so feature
 * data refetches against the new tenant.
 */
export async function switchTenant(tenantId: string): Promise<void> {
  if (env.authMode !== 'core') {
    throw new Error('Tenant switching is only available in SSO (core) mode.');
  }
  const auth = useAuthStore.getState();
  const tenant = auth.linkedTenants.find((t) => t.tenant_id === tenantId);

  // The switch endpoint authenticates with the SSO **session token**, not the
  // tenant access token. Mirror the Angular AuthService: send it as the Bearer
  // and include owner_type + root scope.
  const sessionToken = auth.ssoSessionToken;
  if (!sessionToken) {
    throw new Error('SSO session not available. Please sign in again.');
  }

  const body: Record<string, unknown> = {
    tenant_id: tenantId,
    owner_type: env.portalOwnerType || 'member',
  };
  const rootScope = getEffectiveRootTenantId(env.rootTenantId);
  if (rootScope) body.root_tenant_id = rootScope;

  // Use a raw axios call (not `coreApi`) so its request interceptor can't
  // overwrite the Authorization header with the tenant access token, and so a
  // failure can't trip the coreApi refresh → sign-out interceptor (which was
  // logging the member out on every switch).
  const { data } = await axios.post<CorePortalAuthResponse>(
    `${env.coreApiUrl}/portal-auth/switch-tenant`,
    body,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`,
        'x-app-template-id': env.appTemplateId,
      },
    },
  );

  if (!data.success) throw new Error('Tenant switch failed');

  await applyAuthSuccess(data, auth.rememberSession);

  // Invalidate everything so feature hooks refetch with new tenant context.
  queryClient.clear();
  useExperienceStore.getState().setExperience(null);
  useModuleStore.getState().setConfig(null);

  // If we got nothing back from /switch-tenant we still set tenantId locally
  // so subsequent requests pick up the right header.
  if (!data.tenant_id) auth.setTenantId(tenantId);

  // Surface the local linked-tenant branding immediately so first paint
  // doesn't flash the previous community's colors while domain-config loads.
  if (tenant?.branding) {
    useBrandingStore.getState().setBranding(
      tenant.domain ?? '',
      tenant.branding.name,
      tenant.branding.primary_color,
      tenant.branding,
    );
  }
}

/* ── Signup ───────────────────────────────────────────────────────
 * Mirrors the Angular member-portal flow: it runs against the resolved
 * tenant's regional API (`/public/members/signup/*`), honours the tenant's
 * `authConfig` (allowed identifiers + verification mode), validates
 * email/mobile availability, and confirms ownership via OTP before the
 * account is created. Community/tenant selection happens before this in
 * core auth mode (the signup screen resolves the chosen domain first).
 */

export interface MemberType {
  member_type_id: string;
  code: string;
  name: string;
  description?: string | null;
  display_order?: number;
  flow_template_id?: string;
}

export interface SignupMemberData {
  first_name: string;
  last_name: string;
  phone?: string;
  member_type_id?: string;
  accepted_term_ids?: string[];
}

/** A term-of-service / policy document the resident must accept at signup. */
export interface DomainTerm {
  terms_id: string;
  title: string;
  content_type?: string;
  external_url?: string | null;
  file_path?: string | null;
}

export interface IdentifierAvailability {
  available: boolean;
  message: string;
}

export interface SignupAvailability {
  email?: IdentifierAvailability;
  mobile?: IdentifierAvailability;
}

/** Member types the tenant exposes for self-signup (regional Tenant API). */
export async function fetchMemberTypes(): Promise<MemberType[]> {
  const { data } = await tenantApi.get<{ data?: MemberType[] }>('/public/member-types');
  return data.data ?? [];
}

/** Terms documents linked to a domain config — for the signup terms gate. */
export async function fetchDomainTerms(domainConfigId: string): Promise<DomainTerm[]> {
  const { data } = await tenantApi.get<{ data?: DomainTerm[] }>(
    `/public/domains/${domainConfigId}/terms`,
  );
  return data.data ?? [];
}

/** Check whether an email / mobile is free to register on the active tenant. */
export async function checkSignupAvailability(params: {
  email?: string;
  mobile?: string;
}): Promise<SignupAvailability> {
  const email = params.email?.trim().toLowerCase();
  const mobile = params.mobile?.trim();
  const body: Record<string, string> = {
    ...(email ? { email } : {}),
    ...(mobile ? { mobile } : {}),
  };

  const rootScope = getEffectiveRootTenantId(env.rootTenantId);
  if (env.authMode === 'core' || rootScope) {
    const tenantId = useAuthStore.getState().tenantId;
    const domain = domainForResolution(window.location.hostname);
    const { data } = await coreApi.post<{ data?: SignupAvailability }>(
      '/portal-auth/signup/check-availability',
      {
        ...body,
        ...(tenantId ? { tenant_id: tenantId } : {}),
        ...(rootScope ? { root_tenant_id: rootScope } : {}),
        ...(domain ? { domain } : {}),
        owner_type: env.portalOwnerType ?? 'member',
      },
    );
    return data.data ?? {};
  }

  const { data } = await tenantApi.post<{ data?: SignupAvailability }>(
    '/public/members/signup/check-availability',
    body,
  );
  return data.data ?? {};
}

/** Request a signup OTP for the chosen verification identifier. */
export async function requestSignupOtp(args: {
  method: 'email' | 'mobile';
  email?: string;
  mobile?: string;
  memberData: SignupMemberData;
}): Promise<void> {
  const email = args.email?.trim().toLowerCase();
  const mobile = args.mobile?.trim();
  const identifier = args.method === 'mobile' ? mobile : email;
  await tenantApi.post('/public/members/signup/request-otp', {
    ...(email ? { email } : {}),
    ...(mobile ? { mobile } : {}),
    ...(args.method === 'mobile' ? { mobile: identifier } : { email: identifier }),
    method: args.method,
    memberData: args.memberData,
  });
}

/** Verify a signup OTP without creating the account (first leg of dual verification). */
export async function verifySignupOtp(args: {
  method: 'email' | 'mobile';
  email?: string;
  mobile?: string;
  otpCode: string;
}): Promise<void> {
  await tenantApi.post('/public/members/signup/verify-otp', {
    ...(args.email ? { email: args.email.trim().toLowerCase() } : {}),
    ...(args.mobile ? { mobile: args.mobile.trim() } : {}),
    method: args.method,
    otpCode: args.otpCode.trim(),
  });
}

/** Verify the final signup OTP and create the member account. */
export async function verifySignupAndCreate(args: {
  method: 'email' | 'mobile';
  email?: string;
  mobile?: string;
  otpCode: string;
  password: string;
}): Promise<void> {
  const { data } = await tenantApi.post<{ success: boolean; message?: string }>(
    '/public/members/signup/verify-and-create',
    {
      ...(args.email ? { email: args.email.trim().toLowerCase() } : {}),
      ...(args.mobile ? { mobile: args.mobile.trim() } : {}),
      method: args.method,
      otpCode: args.otpCode.trim(),
      password: args.password,
    },
  );
  if (data && data.success === false) {
    throw new Error(data.message || 'Could not create your account');
  }
}

/**
 * Decide which auth backend to hit. Mirrors the Angular member-portal's
 * `shouldUseCoreSsoGateway()` (auth.service.ts:1156-1158): use Core's SSO
 * gateway whenever the app is in `core` auth mode OR a root-tenant scope
 * is in effect (multi-tenant SSO hub). Tenant-direct otherwise.
 *
 * Used by both forgot-password and reset-password so the two halves of
 * the OTP flow stay on the same backend.
 */
function shouldUseCoreSsoGateway(): boolean {
  if (shouldUseDirectTenantLogin()) return false;
  if (env.authMode === 'core') return true;
  return Boolean(getEffectiveRootTenantId(env.rootTenantId));
}

/**
 * Normalise the identifier for the chosen channel — emails are
 * lower-cased and trimmed; mobile numbers are trimmed only (they're
 * already E.164 from `buildE164`).
 */
function normaliseIdentifier(method: 'email' | 'mobile', raw: string): string {
  const trimmed = raw.trim();
  return method === 'email' ? trimmed.toLowerCase() : trimmed;
}

/**
 * Forgot-password — request a 6-digit OTP through the channel the user
 * picked (email or SMS). Mirrors `AuthService.forgotPassword` from the
 * Angular member-portal and honours the tenant's `authConfig` exactly
 * like the login screen does: the form only renders channels permitted
 * by `authConfig.allowed_identifiers`; the request carries the chosen
 * `method` so the server-side `SignupOTPService` picks the matching
 * template from `authConfig.otp.templates.reset.{email,sms}.template_id`.
 *
 *  - SSO gateway (Core)  → `POST /portal-auth/forgot-password`
 *                          body: { identifier, method, owner_type,
 *                                  root_tenant_id?, domain? }
 *  - Tenant-direct       → `POST /auth/forgot-password`
 *                          body: { identifier, method, owner_type } —
 *                          the controller accepts either `identifier` or
 *                          `email`, and `method='mobile'` routes the OTP
 *                          to SMS.
 */
export async function forgotPassword(values: ForgotValues): Promise<true> {
  const method = values.method;
  const identifier = normaliseIdentifier(method, values.identifier);
  const ownerType = env.portalOwnerType ?? 'member';

  if (shouldUseCoreSsoGateway()) {
    const body: Record<string, unknown> = {
      identifier,
      method,
      owner_type: ownerType,
    };
    const rootScope = getEffectiveRootTenantId(env.rootTenantId);
    if (rootScope) body.root_tenant_id = rootScope;
    const domain = domainForResolution(window.location.hostname);
    if (domain) body.domain = domain;
    await coreApi.post('/portal-auth/forgot-password', body);
    return true;
  }

  await tenantApi.post('/auth/forgot-password', {
    identifier,
    method,
    owner_type: ownerType,
  });
  return true;
}

/**
 * Reset-password — verify the OTP and set a new password atomically.
 * Mirrors `AuthService.resetPassword` (Angular auth.service.ts:1097)
 * with mobile support layered in: `method` decides whether the OTP was
 * sent to email or SMS and which delivery template was used. The server
 * verifies the code against the matching `password_reset` OTP record
 * for that identifier+method, writes the new password, and returns
 * success. The user then signs in manually.
 *
 *  - SSO gateway (Core)  → `POST /portal-auth/reset-password`
 *                          body: { identifier, method, otp, new_password,
 *                                  owner_type, root_tenant_id?, domain? }
 *  - Tenant-direct       → `POST /auth/reset-password`
 *                          body: { identifier, method, otp, new_password }
 */
export async function resetPassword(values: ResetValues): Promise<true> {
  const method = values.method;
  const identifier = normaliseIdentifier(method, values.identifier);
  const ownerType = env.portalOwnerType ?? 'member';

  if (shouldUseCoreSsoGateway()) {
    const body: Record<string, unknown> = {
      identifier,
      method,
      otp: values.otp.trim(),
      new_password: values.password,
      owner_type: ownerType,
    };
    const rootScope = getEffectiveRootTenantId(env.rootTenantId);
    if (rootScope) body.root_tenant_id = rootScope;
    const domain = domainForResolution(window.location.hostname);
    if (domain) body.domain = domain;
    await coreApi.post('/portal-auth/reset-password', body);
    return true;
  }

  await tenantApi.post('/auth/reset-password', {
    identifier,
    method,
    otp: values.otp.trim(),
    new_password: values.password,
  });
  return true;
}
